import { useEffect, useRef } from "react";
import { Chessground } from "@lichess-org/chessground";
import type { Api as CgApi } from "@lichess-org/chessground/api";
import type { Key } from "@lichess-org/chessground/types";
import "@lichess-org/chessground/assets/chessground.base.css";
import "@lichess-org/chessground/assets/chessground.brown.css";
import "@lichess-org/chessground/assets/chessground.cburnett.css";
import type { ChessView } from "../lib/views.js";

interface Props {
  view: ChessView;
  /** Ход разрешён (партия идёт и очередь наша). */
  interactive: boolean;
  onMove(from: string, to: string, promotion?: "q" | "r" | "b" | "n"): void;
}

const RANK_8 = "8";
const RANK_1 = "1";

export function Board({ view, interactive, onMove }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<CgApi | null>(null);
  // Держим свежий обработчик, чтобы не пересоздавать доску на каждый рендер.
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    if (!hostRef.current) return;

    const api = Chessground(hostRef.current, {
      fen: view.fen,
      orientation: view.myColor === "w" ? "white" : "black",
      turnColor: view.myColor === "w" ? "white" : "black",
      animation: { enabled: true, duration: 180 },
      highlight: { lastMove: true, check: true },
      draggable: { enabled: true, showGhost: true },
      // Премувы добавим позже — сперва нужен надёжный обычный ход.
      premovable: { enabled: false },
      movable: {
        free: false,
        color: undefined,
        dests: new Map(),
        events: {
          after: (orig: Key, dest: Key) => {
            const v = viewRef.current;
            const isPromotion =
              v.promotionFrom.includes(orig) &&
              (dest[1] === RANK_8 || dest[1] === RANK_1);
            // Пока всегда ферзь — выбор фигуры добавим отдельным диалогом.
            onMoveRef.current(orig, dest, isPromotion ? "q" : undefined);
          },
        },
      },
    });

    apiRef.current = api;
    return () => {
      api.destroy();
      apiRef.current = null;
    };
  }, []);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const myColor = view.myColor === "w" ? "white" : "black";
    const turnColor = view.turn === view.me ? myColor : myColor === "white" ? "black" : "white";

    const dests = new Map<Key, Key[]>();
    if (interactive) {
      for (const [from, tos] of Object.entries(view.dests)) {
        dests.set(from as Key, tos as Key[]);
      }
    }

    api.set({
      fen: view.fen,
      orientation: myColor,
      turnColor,
      check: view.inCheck,
      lastMove: view.lastMove ? [view.lastMove.from as Key, view.lastMove.to as Key] : undefined,
      movable: {
        free: false,
        color: interactive ? myColor : undefined,
        dests,
      },
    });
  }, [view, interactive]);

  return <div className="board-wrap" ref={hostRef} />;
}
